import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CampsiteBrandBar } from "@/components/branding/CampsiteBrandBar";
import { useBranding } from "@/lib/branding/useBranding";
import { fetchTouristPanelDisplay } from "@/lib/touristPanelApi";
import { TouristPanelDevicePendingScreen } from "@/pages/tourist-panel/components/TouristPanelDevicePendingScreen";
import { TouristPanelMinStayEditor } from "@/pages/tourist-panel/components/TouristPanelMinStayEditor";
import { TouristPanelSameDayCutoffEditor } from "@/pages/tourist-panel/components/TouristPanelSameDayCutoffEditor";
import { TouristPanelSoldOutEditor } from "@/pages/tourist-panel/components/TouristPanelSoldOutEditor";
import { useTouristPanelDevice } from "@/pages/tourist-panel/useTouristPanelDevice";
import type { TouristPanelDisplay } from "@/types/touristPanel";

export function TouristPanelPage() {
  const { workstationCode = "" } = useParams<{ workstationCode: string }>();
  const { siteName, logoUrl } = useBranding();
  const { deviceStatus, loading, error, isApproved, register } =
    useTouristPanelDevice(workstationCode);

  const [display, setDisplay] = useState<TouristPanelDisplay | null>(null);
  const [displayLoading, setDisplayLoading] = useState(false);
  const [displayError, setDisplayError] = useState<string | null>(null);

  const loadDisplay = useCallback(async () => {
    if (!isApproved || !workstationCode) return;
    setDisplayLoading(true);
    setDisplayError(null);
    try {
      const data = await fetchTouristPanelDisplay(workstationCode);
      setDisplay(data);
    } catch (err) {
      setDisplayError(err instanceof Error ? err.message : "Daten konnten nicht geladen werden.");
    } finally {
      setDisplayLoading(false);
    }
  }, [isApproved, workstationCode]);

  useEffect(() => {
    void loadDisplay();
  }, [loadDisplay]);

  if (!workstationCode) {
    return <p className="p-6 text-label-secondary">Kein Arbeitsplatz angegeben.</p>;
  }

  if (!isApproved) {
    return (
      <TouristPanelDevicePendingScreen
        status={deviceStatus}
        loading={loading}
        error={error}
        onRetry={() => void register()}
      />
    );
  }

  const soldOutPageEnabled = display?.pages.some((p) => p.id === "sold_out_nights" && p.enabled);
  const minStayPageEnabled = display?.pages.some((p) => p.id === "min_stay" && p.enabled);
  const sameDayCutoffPageEnabled = display?.pages.some(
    (p) => p.id === "same_day_cutoff" && p.enabled,
  );
  const anyPageEnabled =
    soldOutPageEnabled || minStayPageEnabled || sameDayCutoffPageEnabled;

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <div className="mx-auto mb-8 flex max-w-2xl justify-center">
        <CampsiteBrandBar siteName={siteName} logoUrl={logoUrl} />
      </div>

      {displayLoading && !display ? (
        <p className="text-center text-label-secondary">Laden…</p>
      ) : null}

      {displayError ? (
        <p className="text-center text-sm text-red-600" role="alert">
          {displayError}
        </p>
      ) : null}

      {display && soldOutPageEnabled ? (
        <TouristPanelSoldOutEditor
          workstationCode={workstationCode}
          display={display}
          onSaved={setDisplay}
        />
      ) : null}

      {display && minStayPageEnabled ? (
        <TouristPanelMinStayEditor
          workstationCode={workstationCode}
          display={display}
          onSaved={setDisplay}
        />
      ) : null}

      {display && sameDayCutoffPageEnabled ? (
        <TouristPanelSameDayCutoffEditor
          workstationCode={workstationCode}
          display={display}
          onSaved={setDisplay}
        />
      ) : null}

      {display && !anyPageEnabled ? (
        <p className="text-center text-label-secondary">
          Keine Seiten freigeschaltet. Bitte unter Stammdaten → Schnittstellen → Tourist-Import → Seiten
          aktivieren.
        </p>
      ) : null}
    </div>
  );
}
